import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../api/api.js';
import { FaSpinner, FaTrash, FaFilePdf, FaBed, FaBuilding, FaDoorOpen, FaTimesCircle, FaCheckCircle, FaEdit } from 'react-icons/fa';
import Button from '../../components/common/Button.jsx';
import { Link, useNavigate } from 'react-router-dom';
import EditBookingModal from './EditBookingModal.jsx';
import ConfirmModal from '../../components/common/ConfirmModal.jsx';
import { toast } from 'react-toastify';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetMyBookingsQuery } from '../../redux/api/apiSlice';

const BookingCard = ({ booking, onEdit, onDelete, onDownloadPdf, navigateToEvent }) => {
    const t = useTranslation();

    const getStatusStyles = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-100 border-green-500 text-green-700';
            case 'pending': return 'bg-yellow-100 border-yellow-500 text-yellow-700';
            case 'declined': return 'bg-red-100 border-red-500 text-red-700';
            default: return 'bg-gray-100 border-gray-400 text-gray-700';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'approved': return t.myBookings.status.approved;
            case 'pending': return t.myBookings.status.pending;
            case 'declined': return t.myBookings.status.declined;
            default: return status;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-5 rounded-2xl shadow-soft border-l-4 transition-all duration-300 ${getStatusStyles(booking.status)}`}
        >
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3
                        onClick={() => navigateToEvent(booking.eventId)}
                        className="text-xl font-bold font-heading text-highlight cursor-pointer hover:underline transition-colors"
                    >
                        {booking.eventId?.name || t.myBookings.card.details}
                    </h3>
                    <p className="text-xs font-mono text-gray-500 mt-1">{booking.bookingNumber}</p>
                    <div className={`flex items-center space-x-2 font-semibold capitalize mt-1 text-base`}>
                        {booking.status === 'approved' && <FaCheckCircle className="text-green-500" />}
                        {booking.status === 'declined' && <FaTimesCircle className="text-red-500" />}
                        <span>{getStatusText(booking.status)}</span>
                    </div>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-background/50 text-gray-700 shadow-inner">
                    {t.myBookings.card.requested} {new Date(booking.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                </span>
            </div>

            {booking.status === 'approved' && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg shadow-inner border border-green-200">
                    <div className="flex items-center space-x-2 text-green-800 mb-2">
                        <FaCheckCircle className="text-xl" />
                        <h4 className="font-bold font-heading text-lg">{t.myBookings.card.allocation}</h4>
                    </div>
                    {booking.showAllocationDetails && booking.allocations?.length > 0 ? (
                        <div className="space-y-2">
                            {booking.allocations.map((alloc, index) => (
                                <div key={index} className="flex items-center space-x-4 text-sm text-green-700 bg-white/50 p-2 rounded-md">
                                    <span className="font-semibold">{booking.formData?.people?.[index]?.name || `Person ${index + 1}`}:</span>
                                    <span className="flex items-center"><FaBuilding className="mr-1" /> {alloc.buildingId?.name || 'N/A'}</span>
                                    <span className="flex items-center"><FaDoorOpen className="mr-1" /> Room {alloc.roomId?.roomNumber || 'N/A'}</span>
                                    <span className="flex items-center"><FaBed className="mr-1" /> {alloc.bedId?.name || 'N/A'}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-md font-medium text-green-700">
                            Your booking has been confirmed!
                        </p>
                    )}
                    <p className="mt-2 text-sm font-bold text-highlight bg-highlight/10 p-2 rounded-md border border-highlight/20">
                        Note: Please report to reception/counter at the time of arrival!
                    </p>
                </div>
            )}

            {booking.status === 'declined' && <p className="mt-4 text-sm font-medium text-red-700">{t.myBookings.card.declinedMsg}</p>}
            {booking.status === 'pending' && <p className="mt-4 text-sm font-medium text-yellow-700">{t.myBookings.card.pendingMsg}</p>}


            <div className="mt-6 flex flex-col sm:flex-row justify-end items-center space-y-2 sm:space-y-0 sm:space-x-2">
                {booking.status === 'approved' && (
                    <Button onClick={() => onDownloadPdf(booking)} className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white text-sm py-2">
                        <FaFilePdf className="inline mr-1" /> {t.myBookings.card.downloadPass}
                    </Button>
                )}
                {(booking.status === 'pending' || booking.status === 'approved') && new Date(booking.eventId?.bookingEndDate) >= new Date() && (
                    <Button onClick={() => onEdit(booking)} className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white text-sm py-2">
                        <FaEdit className="inline mr-1" /> {t.myBookings.card.edit}
                    </Button>
                )}
                <Button onClick={() => onDelete(booking._id)} className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white text-sm py-2">
                    <FaTrash className="inline mr-1" /> {t.myBookings.card.withdraw}
                </Button>
            </div>
        </motion.div>
    );
};

const MyBookings = () => {
    const t = useTranslation();
    const token = useSelector((state) => state.auth.token);
    const { data: rawBookings, isLoading: loading, isError, refetch } = useGetMyBookingsQuery(undefined, { skip: !token });
    const bookings = useMemo(() => Array.isArray(rawBookings) ? rawBookings : [], [rawBookings]);
    const error = isError ? t.myBookings.actions.fetchFail : null;

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [bookingToDelete, setBookingToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!isEditModalOpen) refetch();
    }, [isEditModalOpen, refetch]);

    const executeDeleteBooking = async () => {
        if (!bookingToDelete) return;
        try {
            await api.delete(`/bookings/delete/${bookingToDelete}`);
            refetch();
            toast.success(t.myBookings.actions.withdrawSuccess);
        } catch (err) {
            const msg = err.response?.data?.message || t.myBookings.actions.withdrawFail;
            toast.error(msg);
        } finally {
            setBookingToDelete(null);
        }
    };

    const handleDeleteClick = (bookingId) => {
        setBookingToDelete(bookingId);
    };

    const handleEdit = (booking) => {
        setSelectedBooking(booking);
        setIsEditModalOpen(true);
    };

    const handleUpdateSuccess = () => {
        setIsEditModalOpen(false);
        setSelectedBooking(null);
        toast.success(t.myBookings.actions.updateSuccess);
        refetch();
    };

    const navigateToEvent = (event) => {
        if (event?._id) navigate(`/calendar?event=${event._id}`);
    };

    const categorizedBookings = useMemo(() => {
        const now = new Date();
        const oldBookings = [];
        const newBookings = [];

        bookings.forEach(b => {
            const eventEnd = new Date(b.eventId?.endDate);
            // If event end date is in the past, it's "old".
            // We assume end of day for the end date if it's just a date string, but Date object comparison usually works fine.
            if (eventEnd < now) {
                oldBookings.push(b);
            } else {
                newBookings.push(b);
            }
        });

        // Sort new bookings: nearest start date first
        newBookings.sort((a, b) => new Date(a.eventId?.startDate) - new Date(b.eventId?.startDate));

        // Sort old bookings: most recent start date first (descending)
        oldBookings.sort((a, b) => new Date(b.eventId?.startDate) - new Date(a.eventId?.startDate));

        return { newBookings, oldBookings };
    }, [bookings]);

    if (loading) return <div className="flex justify-center items-center h-screen bg-neutral"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;
    if (error) return <div className="text-center mt-10 p-4 text-highlight bg-highlight/10 rounded-xl">{error}</div>;

    if (bookings.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center h-screen text-center px-4 bg-neutral font-body">
                <FaTimesCircle className="text-6xl text-background mb-4" />
                <h2 className="text-2xl font-bold font-heading text-primaryDark mb-2">{t.myBookings.noBookings.title}</h2>
                <p className="text-gray-700">{t.myBookings.noBookings.desc}</p>
                <Link to="/calendar">
                    <Button className="mt-6 bg-primary hover:bg-primaryDark text-white">{t.myBookings.noBookings.button}</Button>
                </Link>
            </div>
        );
    }

    return (
        <>
            <div className="p-4 md:p-8 max-w-5xl mx-auto min-h-screen bg-neutral font-body">
                <h2 className="text-3xl font-bold font-heading mb-6 text-primaryDark border-b-2 border-primary pb-2 text-center">{t.myBookings.title}</h2>

                <input
                    type="text"
                    placeholder={t.myBookings.searchPlaceholder}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full p-3 mb-6 border border-background rounded-lg focus:ring-primary focus:border-primary transition-colors"
                />

                {['newBookings', 'oldBookings'].map(category => {
                    const filtered = categorizedBookings[category].filter(b =>
                        (b.bookingNumber || '').includes(searchQuery) || (b.eventId?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
                    );
                    if (filtered.length === 0) return null;

                    const titleColor = category === 'newBookings' ? 'text-highlight' : 'text-gray-500';
                    const titleText = category === 'newBookings' ? "Current & Upcoming Bookings" : "Past Bookings";

                    return (
                        <div key={category} className="mb-8">
                            <h3 className={`text-2xl font-semibold font-heading mb-4 ${titleColor}`}>{titleText}</h3>
                            <div className="space-y-4">
                                {filtered.map(b => (
                                    <BookingCard
                                        key={b._id}
                                        booking={b}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteClick}
                                        onDownloadPdf={async booking => {
                                            const res = await api.get(`/bookings/pdf/${booking._id}`, { responseType: 'blob' });
                                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', `Booking-Pass-${booking.bookingNumber}.pdf`);
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                            window.URL.revokeObjectURL(url);
                                        }}
                                        navigateToEvent={navigateToEvent}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}

                {['newBookings', 'oldBookings'].every(category =>
                    categorizedBookings[category].filter(b =>
                        (b.bookingNumber || '').includes(searchQuery) || (b.eventId?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
                    ).length === 0
                ) && searchQuery && (
                        <div className="text-center mt-10 text-gray-700">
                            {t.myBookings.noMatch}
                        </div>
                    )}
            </div>

            <AnimatePresence>
                {isEditModalOpen && selectedBooking && (
                    <EditBookingModal
                        booking={selectedBooking}
                        onClose={() => setIsEditModalOpen(false)}
                        onUpdate={handleUpdateSuccess}
                    />
                )}
            </AnimatePresence>

            <ConfirmModal
                isOpen={!!bookingToDelete}
                onClose={() => setBookingToDelete(null)}
                onConfirm={executeDeleteBooking}
                title="Withdraw Booking"
                message={t.myBookings.actions.withdrawConfirm}
                confirmText={t.myBookings.card.withdraw}
                cancelText={t.common.cancel}
                isDanger={true}
            />
        </>
    );
};

export default MyBookings;
